import { Fragment, useEffect, useState } from "react";
import { Menu, Popover, Transition } from "@headlessui/react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import Alert from "../../components/alert";
import { XIcon } from "@heroicons/react/solid";
import { connect } from "react-redux";
import { logout } from "../../redux/actions/auth";
import { search_products } from "../../redux/actions/products";
import { IconSearch } from "@tabler/icons-react";
import SubscribeForm from "./SubscribeForm";
import ChatBubble from "../ChatBubble";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Navbar({
  isAuthenticated,
  logout,
  search_products,
  profile,
  products,
}) {
  const navigate = useNavigate();
  const [redirect, setRedirect] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [formData, setFormData] = useState({
    search: "",
  });
  const { search } = formData;
  const [isChatOpen, setIsChatOpen] = useState(false);
  const openChat = () => {
    setIsChatOpen(true);
  };
  // Estados para evitar múltiples búsquedas consecutivas
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearched, setLastSearched] = useState("");

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    // Si ya se está buscando o el query no ha cambiado, no se vuelve a disparar
    if (isSearching || search === lastSearched) {
      return;
    }
    setIsSearching(true);
    setLastSearched(search);
    navigate(`/?search=${encodeURIComponent(search)}`);
    search_products(search);
    // Se reactiva el botón luego de 500ms (o el tiempo que consideres adecuado)
    setTimeout(() => {
      setIsSearching(false);
    }, 500);
  };

  const onClear = () => {
    setFormData({ search: "" });
    navigate(`/?search=`);
    search_products("");
    setLastSearched("");
  };

  const logoutHandler = () => {
    setIsPopoverOpen(false);
    logout();
    setRedirect(true);
  };

  useEffect(() => {
    if (redirect) {
      navigate("/");
    }
  }, [redirect, navigate]);

  const authLinks = (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex justify-center w-full rounded-full text-sm font-medium text-gray-700 focus:outline-none">
          <span className="inline-block h-10 w-10 rounded-full overflow-hidden bg-gray-100">
            {profile?.photo ? (
              <img
                src={`${process.env.REACT_APP_API_URL}${profile.photo}`}
                alt="Profile"
                className="h-full w-full object-cover"
              />
            ) : (
              <svg
                className="h-full w-full text-gray-300"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993z" />
                <path d="M16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            )}
          </span>
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white focus:outline-none z-40">
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/dashboard"
                  className={classNames(
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                    "block px-4 py-2 text-sm"
                  )}
                >
                  Dashboard
                </Link>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={logoutHandler}
                  className={classNames(
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                    "block w-full text-left px-4 py-2 text-sm"
                  )}
                >
                  Logout
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );

  const guestLinks = (
    <div className="flex items-center space-x-4">
      <Link
        to="/login"
        className="text-base font-medium text-white hover:text-gray-900"
      >
        Login
      </Link>
      <Link
        to="/signup"
        className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-emerald-600 hover:bg-emerald-700 "
      >
        Sign up
      </Link>
    </div>
  );

  return (
    <>
      <Popover className="bg-white" open={isPopoverOpen}>
        <div className="max-w-7xl mx-auto px-1 py-5 sm:px-6 ">
          {/* MOBILE VIEW */}
          <div className="md:hidden flex flex-col space-y-4">
            {/* ROW 1: TITLE + AUTH/SUBSCRIBE (SIDE BY SIDE) */}
            <div className="flex items-center justify-between px-3">
              <Link to="/">
                <h1 className="text-xl font-bold text-black whitespace-nowrap">
                  HISTORI AI
                </h1>
              </Link>
              <button  onClick={openChat} className="ml-3 bg-gray-800 dark:bg-gray-700 text-white text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center">
                AI
              </button>
              <div className="flex items-center flex-grow justify-end">
                {/* Si está autenticado, muestra authLinks; si no, muestra SubscribeForm */}
                {isAuthenticated ? authLinks : <SubscribeForm />}
              </div>
            </div>

            {/* ROW 2: SEARCH (solo si !products) */}
            {!products && (
              <div className="flex items-center px-2">
                <form
                  onSubmit={onSubmit}
                  className="flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2 w-full"
                >
                  <button onClick={onSubmit} disabled={isSearching}>
                    <IconSearch className="text-gray-500" />
                  </button>
                  <input
                    type="text"
                    placeholder="Search for products type and search."
                    name="search"
                    value={search}
                    onChange={onChange}
                    autoComplete="off"
                    className="flex-grow bg-white border-none focus:outline-none text-gray-700 placeholder-gray-500 text-sm "
                  />

                  {search && (
                    <button
                      type="button"
                      onClick={onClear}
                      className="absolute right-10 text-gray-500 hover:text-gray-700"
                    >
                      {/* Icono "x" usando SVG */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </form>
              </div>
            )}
          </div>

          {/* DESKTOP VIEW */}
          <div className="hidden md:flex items-center justify-between w-full">
            {/* Izquierda: Logo e input */}
            <div className="flex items-center space-x-6">
              <Link to="/">
                <h1 className="text-2xl font-bold text-black whitespace-nowrap">
                  HISTORI.AI
                </h1>
              </Link>
              <button onClick={openChat} className="ml-3 bg-gray-800 dark:bg-gray-700 text-white text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center">
                AI
              </button>
              {/* Solo mostrar el formulario si NO hay products */}
              {!products && (
                <form
                  onSubmit={onSubmit}
                  className="
          relative 
          flex items-center 
          w-full max-w-xl
          bg-white border border-gray-300 
          rounded-full 
          px-4 py-2
          space-x-2
        "
                >
                  {/* Botón de búsqueda con ícono (lado izquierdo) */}
                  <button
                    type="submit"
                    className="focus:outline-none"
                    disabled={isSearching}
                  >
                    <IconSearch className="text-gray-700 w-5 h-5" />
                  </button>

                  {/* Input que se expande */}
                  <input
                    type="text"
                    placeholder="Search for products type and search."
                    name="search"
                    value={search}
                    onChange={onChange}
                    autoComplete="off"
                    className="
            flex-grow 
            bg-transparent 
            focus:outline-none 
            text-gray-700 
            text-base
          "
                  />

                  {/* Botón Clear (solo si 'search' tiene texto) */}
                  {search && (
                    <button
                      type="button"
                      onClick={onClear}
                      className="
              focus:outline-none 
              text-gray-500 
              hover:text-gray-700
            "
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </form>
              )}
            </div>

            {/* Derecha: links o botón (ej. SubscribeForm o authLinks) */}
            {isAuthenticated ? authLinks : <SubscribeForm />}
          </div>
        </div>
        <Transition
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            focus
            className="absolute top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50"
          >
            <div className="rounded-lg divide-y-2 divide-gray-50">
              <div className="pt-5 pb-6 px-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">HISTORI.AI</h2>
                  </div>
                  <div className="-mr-2">
                    <Popover.Button className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 focus:outline-none">
                      <XIcon className="h-6 w-6" />
                    </Popover.Button>
                  </div>
                </div>
                <div className="mt-4 flex">
                  {!products && (
                    <>
                      <form
                        onSubmit={onSubmit}
                        className="relative flex items-center bg-white border-b border-gray-300 w-full max-w-md"
                      >
                        <button
                          onClick={onSubmit}
                          className="ml-2 bg-white border border-gray-300 rounded-lg px-3 py-2"
                          disabled={isSearching}
                        >
                          <IconSearch className="text-gray-500" />
                        </button>
                        <input
                          type="text"
                          placeholder="Search for products type and search."
                          autoComplete="off"
                          name="search"
                          value={search}
                          onChange={onChange}
                          className="flex-grow px-2 py-1 bg-white border-none focus:outline-none text-gray-700"
                        />
                        {search && (
                          <button
                            type="button"
                            onClick={onClear}
                            className="absolute right-10 text-gray-500 hover:text-gray-700"
                          >
                            {/* Icono "x" usando SVG */}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                      </form>
                    </>
                  )}
                </div>
              </div>
              <div className="py-6 px-5 space-y-4">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="block text-base font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={logoutHandler}
                      className="block w-full text-left text-base font-medium text-red-600 hover:text-red-700"
                    >
                      Cerrar sesión
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/signup"
                      className="block w-full text-center px-4 py-2 border border-transparent rounded-md text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      Regístrate
                    </Link>
                    <Link
                      to="/login"
                      className="block text-center text-base font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      Inicia sesión
                    </Link>
                  </>
                )}
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
      <Alert />
      {isChatOpen && (
        <div className="fixed bottom-4 right-4 z-50">
          <ChatBubble
            onClose={() => setIsChatOpen(false)}
          />
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  isAuthenticated: state.Auth.isAuthenticated,
  total_items: state.Cart.total_items,
  profile: state.Profile.profile,
});

export default connect(mapStateToProps, {
  logout,
  search_products,
})(Navbar);
